
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SecurityEnhancedAuth } from './SecurityEnhancedAuth';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from "@/lib/utils";

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email').min(1, 'Email is required'),
});

interface ForgotPasswordFormProps {
  onBackToLogin?: () => void;
}

export function ForgotPasswordForm({ onBackToLogin }: ForgotPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const form = useForm<{ email: string }>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: { email: string }) => {
    setIsLoading(true);

    try {
      const { error } = await SecurityEnhancedAuth.resetPassword(data.email);

      if (error) {
        let errorMessage = 'Failed to send password reset email. Please try again.';

        if (error.message.includes('User not found')) {
          errorMessage = 'No account found with this email address.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email address first before resetting your password.';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Too many password reset requests. Please wait before trying again.';
        }

        toast({
          title: "Reset Failed",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      setIsSuccess(true);

      toast({
        title: "Reset Email Sent!",
        description: "Please check your email for password reset instructions.",
        duration: 6000,
      });

    } catch (error: any) {
      toast({
        title: "Reset Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="space-y-4 text-center">
        <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-center mb-3">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-green-800 mb-1">Check Your Email</h3>
          <p className="text-sm text-green-600 mb-4">
            We've sent password reset instructions to your email address.
          </p>
          {onBackToLogin && (
            <Button 
              onClick={onBackToLogin} 
              variant="outline"
              className="text-green-700 border-green-300 hover:bg-green-50"
            >
              Back to Sign In
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Email Address</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter your email address" 
                  {...field} 
                  type="email"
                  autoComplete="email"
                  className={cn(
                    "transition-all focus-within:ring-2 focus-within:ring-brand-400",
                    isMobile ? "h-12 text-base px-4" : ""
                  )}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        
        <div className="flex flex-col space-y-3">
          <Button 
            type="submit" 
            className="w-full bg-brand-600 hover:bg-brand-700 text-white"
            size={isMobile ? "lg" : "default"}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending Reset Email...
              </span>
            ) : (
              'Send Reset Email'
            )}
          </Button>
          
          {onBackToLogin && (
            <Button 
              type="button" 
              variant="link" 
              onClick={onBackToLogin}
              className="text-brand-600 hover:text-brand-700 p-0 h-auto"
            >
              Back to Sign In
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
